import type { FormCategory } from './registry.js';
import { extractField } from './extractor.js';
import { getTrackingData, setTrackingField } from '../cookie/tracking-cookie.js';
import { getTaleezUser } from '../cookie/taleez-user.js';
import { buildHubSpotIdentifyPayload } from '../identify/hubspot-builder.js';
import { buildAmplitudeIdentifyCall } from '../identify/amplitude-builder.js';
import { hsqIdentify, hsqTrackPageView } from '../services/hubspot.js';
import { amplitudeIdentify, setAmplitudeUserId } from '../services/amplitude.js';
import { logger } from '../utils/logger.js';

/**
 * Attache un submit handler sur un formulaire HSFC détecté.
 *
 * Au submit :
 *  1. Extrait email, phone, firstname, lastname, company
 *  2. Sauvegarde dans le cookie tracking
 *  3. Pose le user_id Amplitude (hash email, fire-and-forget)
 *  4. Construit et envoie _hsq identify + trackPageView (async, hash email)
 *  5. Construit et envoie amplitude.identify (si SDK dispo)
 *  6. Laisse le formulaire submit normalement (PAS de preventDefault)
 */
export function attachSubmitHandler(
  form: HTMLFormElement,
  formId: string,
  category: FormCategory,
): void {
  form.addEventListener('submit', () => {
    logger.info('Submit intercepté:', category, formId);

    // 1. Extraction des champs
    const email = extractField(form, 'email');
    const phone = extractField(form, 'phone');
    const firstname = extractField(form, 'firstname');
    const lastname = extractField(form, 'lastname');
    const company = extractField(form, 'company');

    // 2. Sauvegarde cookie
    if (email) {
      setTrackingField('email', email);
      setTrackingField('email_identify_hs', email);
    }
    if (phone) setTrackingField('phone', phone);
    if (firstname) setTrackingField('firstname', firstname);
    if (lastname) setTrackingField('lastname', lastname);
    if (company) setTrackingField('company', company);
    setTrackingField('last_form_category', category);
    setTrackingField('last_form_id', formId);
    setTrackingField('last_form_submission_time', Date.now());

    // Log du cookie complet après les setTrackingField
    logger.info('Cookie juste avant identify:', getTrackingData());

    // 3. Poser le user_id Amplitude AVANT les identify (fire-and-forget)
    const taleezUser = getTaleezUser();
    const finalEmail = taleezUser?.mail ?? email;
    if (window.amplitude) {
      setAmplitudeUserId(finalEmail).catch((err) =>
        logger.error('setAmplitudeUserId au submit a échoué:', err),
      );
    }

    // 4. _hsq identify + trackPageView (async — hash email)
    buildHubSpotIdentifyPayload('submit')
      .then((hsPayload) => {
        hsqIdentify(hsPayload);
        hsqTrackPageView();
      })
      .catch((error) => {
        logger.error('Erreur construction payload HubSpot au submit:', error);
      });

    // 5. Amplitude identify (si SDK dispo)
    if (window.amplitude) {
      amplitudeIdentify(buildAmplitudeIdentifyCall());
    } else {
      logger.warn('Amplitude pas encore chargé au moment du submit');
    }

    // 6. PAS de preventDefault — le form doit submit normalement vers HubSpot
  });
}
